import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { RouteErrorBoundary } from '@/utils/errorBoundary'
import ConsoleCleaner from '@/components/diagnostics/ConsoleCleaner'
// import NavigationTester from '@/components/layout/NavigationTester' - Removed as requested
import { Suspense, lazy, useEffect, useRef } from 'react'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { useAuth } from '@/hooks/useAuth'
import { usePWA } from '@/hooks/usePWA'
import MobileLayout from '@/components/layout/MobileLayout'
import { PWAStatusBar } from '@/components/pwa/PWAPrompts'
import { PushPermissionBanner } from '@/components/notifications/PushPermissionBanner'
import { getNotificationService } from '@/services/notification.service'

// Lazy load components with performance-optimized imports
const Dashboard = lazy(() => import('@/pages/Dashboard').then(module => ({ default: module.Dashboard })))

// Customer management components - group for code splitting
const CustomerList = lazy(() => import('@/pages/customers/CustomerList').then(module => ({ default: module.CustomerList })))
const CustomerDetail = lazy(() => import('@/pages/customers/CustomerDetail').then(module => ({ default: module.CustomerDetail })))

// Project management components - group for code splitting  
const ProjectList = lazy(() => import('@/pages/projects/ProjectList').then(module => ({ default: module.ProjectList })))
const ProjectDetail = lazy(() => import('@/pages/projects/ProjectDetail').then(module => ({ default: module.ProjectDetail })))
const ProjectEdit = lazy(() => import('@/pages/projects/ProjectEdit').then(module => ({ default: module.ProjectEdit })))
const ProjectCreate = lazy(() => import('@/pages/projects/ProjectCreate').then(module => ({ default: module.ProjectCreate })))

// Appointment management components - group for code splitting
const Appointments = lazy(() => import('@/pages/Appointments').then(module => ({ default: module.Appointments })))
const AppointmentDetail = lazy(() => import('@/pages/appointments/AppointmentDetail').then(module => ({ default: module.AppointmentDetail })))

// Analytics components - group for code splitting
const DashboardHub = lazy(() => import('@/pages/analytics/DashboardHub').then(module => ({ default: module.DashboardHub })))
const ExecutiveDashboard = lazy(() => import('@/pages/analytics/ExecutiveDashboard').then(module => ({ default: module.ExecutiveDashboard })))
const OperationalDashboard = lazy(() => import('@/pages/analytics/OperationalDashboard').then(module => ({ default: module.OperationalDashboard })))
const AnalyticsExplorer = lazy(() => import('@/pages/analytics/AnalyticsExplorer').then(module => ({ default: module.AnalyticsExplorer })))

// Profile page - lighter weight
const Profile = lazy(() => import('@/pages/Profile').then(module => ({ default: module.Profile })))

// Settings pages
const NotificationSettings = lazy(() => import('@/components/notifications/NotificationSettings').then(module => ({ default: module.default })))

// Developer admin pages - lazy loaded, role-gated
const DeveloperOverview = lazy(() => import('@/pages/developer/DeveloperOverview'))
const DeveloperCustomers = lazy(() => import('@/pages/developer/DeveloperCustomers'))
const DeveloperBilling = lazy(() => import('@/pages/developer/DeveloperBilling'))
const DeveloperShopify = lazy(() => import('@/pages/developer/DeveloperShopify'))
const ShopifyCallback = lazy(() => import('@/pages/shopify/ShopifyCallback'))

// Client portal pages - lazy loaded, separate layout
const ClientLogin = lazy(() => import('@/pages/client/ClientLogin').then(m => ({ default: m.ClientLogin })))
const ClientRegister = lazy(() => import('@/pages/client/ClientRegister').then(m => ({ default: m.ClientRegister })))
const ClientResetPassword = lazy(() => import('@/pages/client/ClientResetPassword').then(m => ({ default: m.ClientResetPassword })))
const ClientDashboard = lazy(() => import('@/pages/client/ClientDashboard').then(m => ({ default: m.ClientDashboard })))
const ClientCommission = lazy(() => import('@/pages/client/ClientCommission').then(m => ({ default: m.ClientCommission })))
const ClientApprovals = lazy(() => import('@/pages/client/ClientApprovals').then(m => ({ default: m.ClientApprovals })))
const ClientAppointments = lazy(() => import('@/pages/client/ClientAppointments').then(m => ({ default: m.ClientAppointments })))
const ClientFeedback = lazy(() => import('@/pages/client/ClientFeedback').then(m => ({ default: m.ClientFeedback })))
const ClientProfile = lazy(() => import('@/pages/client/ClientProfile').then(m => ({ default: m.ClientProfile })))
const ClientDocuments = lazy(() => import('@/pages/client/ClientDocuments').then(m => ({ default: m.ClientDocuments })))
const ClientNotifications = lazy(() => import('@/pages/client/ClientNotifications').then(m => ({ default: m.ClientNotifications })))

// Legal pages - lazy loaded, public
const PrivacyPolicy = lazy(() => import('@/pages/legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })))
const TermsOfService = lazy(() => import('@/pages/legal/TermsOfService').then(m => ({ default: m.TermsOfService })))
const Support = lazy(() => import('@/pages/legal/Support').then(m => ({ default: m.Support })))

// Auth components - immediate load for better UX
import { Login } from '@/pages/auth/Login'
import { Register } from '@/pages/auth/Register'
import { ForgotPassword } from '@/pages/auth/ForgotPassword'

// Cookie consent banner
import { CookieConsent } from '@/components/ui/cookie-consent'

// Guided tour
import { TourProvider } from '@/components/tour/TourProvider'

// Premium Loading fallback component
const LoadingFallback = ({ message = 'Loading...' }: { message?: string }) => (
  <div
    data-testid="loading-fallback"
    className="flex items-center justify-center min-h-screen bg-gradient-to-br from-background via-background/98 to-background/95 relative overflow-hidden"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-primary/1 via-transparent to-accent/1 pointer-events-none" />
    <div className="flex flex-col items-center space-y-8 p-12 relative z-10">
      {/* Premium Spinner */}
      <div className="relative">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary/20">
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary shadow-glow animate-pulse" />
        </div>
        
        {/* Orbital Elements */}
        <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s', animationDirection: 'reverse' }}>
          <div className="absolute -top-1 left-1/2 w-2 h-2 -translate-x-1/2 rounded-full bg-gradient-to-r from-primary to-accent-foreground shadow-glow animate-pulse" />
        </div>
        <div className="absolute inset-0 animate-spin" style={{ animationDuration: '2s' }}>
          <div className="absolute -bottom-1 left-1/2 w-1.5 h-1.5 -translate-x-1/2 rounded-full bg-gradient-to-r from-accent-foreground to-primary shadow-glow animate-pulse" />
        </div>
      </div>
      
      {/* Premium Loading Text */}
      <div className="text-center space-y-3">
        <p className="text-xl font-bold text-foreground">
          {message}
        </p>
        
        {/* Animated Dots */}
        <div className="flex justify-center space-x-1">
          <div className="w-2 h-2 rounded-full bg-primary animate-bounce" />
          <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0.1s' }} />
          <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0.2s' }} />
        </div>
      </div>
      
      {/* Premium Glow Effect */}
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary/5 via-transparent to-accent/5 blur-3xl animate-pulse pointer-events-none" />
    </div>
  </div>
)

function App() {
  const { checkAuth, isLoading, isAuthenticated } = useAuth();
  const { initialize: initializePWA, isInitialized: isPWAInitialized } = usePWA();
  const hasInitializedAuth = useRef(false);
  const hasInitializedPWA = useRef(false);
  const hasInitializedNotifications = useRef(false);
  
  // Initialize PWA services on app startup
  useEffect(() => {
    if (!hasInitializedPWA.current) {
      hasInitializedPWA.current = true;
      initializePWA().catch((err) => {
        console.warn('PWA initialization failed:', err);
        hasInitializedPWA.current = false; // Allow retry if this fails
      });
    }
  }, [initializePWA]);

  // Initialize notification service for authenticated users
  useEffect(() => {
    if (isAuthenticated && !hasInitializedNotifications.current) {
      hasInitializedNotifications.current = true;
      const notificationService = getNotificationService();
      notificationService.initializeForUser().catch((err) => {
        console.warn('Notification service initialization failed:', err);
        hasInitializedNotifications.current = false; // Allow retry if this fails
      });
    } else if (!isAuthenticated && hasInitializedNotifications.current) {
      // Reset when user logs out
      hasInitializedNotifications.current = false;
    }
  }, [isAuthenticated]);

  // Check authentication status on app startup - only once
  // Skip on public auth pages to avoid unnecessary 401 → refresh → session-expired chain
  useEffect(() => {
    if (!hasInitializedAuth.current) {
      hasInitializedAuth.current = true;

      const path = window.location.pathname;
      const isPublicAuthPage =
        path === '/login' || path === '/register' || path === '/forgot-password' ||
        path === '/client/login' || path === '/client/register' || path === '/client/reset-password';

      if (isPublicAuthPage) {
        return; // No point checking auth on login/register pages
      }

      checkAuth().catch((err) => {
        // Don't throw error - user might just not be logged in
        console.debug('Auth check failed (expected if not logged in):', err);
      });
    }
  }, []); // Empty dependency array ensures this runs only once
  
  // Show loading screen only during the very first auth check
  if (isLoading && !hasInitializedAuth.current) {
    return <LoadingFallback message="Checking authentication..." />;
  }
  
  return (
    <Router
      future={{
        v7_relativeSplatPath: true,
      }}
    >
      <RouteErrorBoundary>
        <TourProvider>
        <MobileLayout
        showNavigation={isAuthenticated}
        showStatusBar
        allowPullToRefresh
      >
        <PWAStatusBar />
        {isAuthenticated && <PushPermissionBanner showOnlyIfNeeded />}
        <main id="main-content" role="main">
          <Suspense fallback={<LoadingFallback />}>
          <Routes>
          {/* Auth routes - no lazy loading for better UX */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Legal pages — public, lazy loaded */}
          <Route path="/privacy" element={<Suspense fallback={<LoadingFallback />}><PrivacyPolicy /></Suspense>} />
          <Route path="/terms" element={<Suspense fallback={<LoadingFallback />}><TermsOfService /></Suspense>} />
          <Route path="/support" element={<Suspense fallback={<LoadingFallback />}><Support /></Suspense>} />

          {/* Client Portal routes — separate layout, no sidebar */}
          <Route path="/client/login" element={<Suspense fallback={<LoadingFallback />}><ClientLogin /></Suspense>} />
          <Route path="/client/register" element={<Suspense fallback={<LoadingFallback />}><ClientRegister /></Suspense>} />
          <Route path="/client/reset-password" element={<Suspense fallback={<LoadingFallback />}><ClientResetPassword /></Suspense>} />
          <Route path="/client/dashboard" element={<Suspense fallback={<LoadingFallback />}><ClientDashboard /></Suspense>} />
          <Route path="/client/commissions/:id" element={<Suspense fallback={<LoadingFallback />}><ClientCommission /></Suspense>} />
          <Route path="/client/approvals" element={<Suspense fallback={<LoadingFallback />}><ClientApprovals /></Suspense>} />
          <Route path="/client/appointments" element={<Suspense fallback={<LoadingFallback />}><ClientAppointments /></Suspense>} />
          <Route path="/client/feedback" element={<Suspense fallback={<LoadingFallback />}><ClientFeedback /></Suspense>} />
          <Route path="/client/profile" element={<Suspense fallback={<LoadingFallback />}><ClientProfile /></Suspense>} />
          <Route path="/client/documents" element={<Suspense fallback={<LoadingFallback />}><ClientDocuments /></Suspense>} />
          <Route path="/client/notifications" element={<Suspense fallback={<LoadingFallback />}><ClientNotifications /></Suspense>} />
          <Route path="/client/settings" element={<Suspense fallback={<LoadingFallback />}><ClientProfile /></Suspense>} />

          {/* Dashboard routes - lazy loaded with custom loading */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Dashboard..." />}>
                  <Dashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Dashboard..." />}>
                  <Dashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Customer management routes - lazy loaded as a group */}
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Clients..." />}>
                  <CustomerList />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Client Details..." />}>
                  <CustomerDetail />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Project management routes - lazy loaded as a group */}
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Commissions..." />}>
                  <ProjectList />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/new"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Project Creator..." />}>
                  <ProjectCreate />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Commission Details..." />}>
                  <ProjectDetail />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id/edit"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Project Editor..." />}>
                  <ProjectEdit />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Appointment management routes - lazy loaded as a group */}
          <Route
            path="/appointments"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Consultations..." />}>
                  <Appointments />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/appointments/:id"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Consultation Details..." />}>
                  <AppointmentDetail />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Profile route - lazy loaded */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Profile..." />}>
                  <Profile />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Analytics routes - lazy loaded as a group */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Analytics Hub..." />}>
                  <DashboardHub />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/executive"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Executive Dashboard..." />}>
                  <ExecutiveDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/operational"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Operational Dashboard..." />}>
                  <OperationalDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/explorer"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Analytics Explorer..." />}>
                  <AnalyticsExplorer />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Settings routes */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Settings..." />}>
                  <Profile />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback message="Loading Notification Settings..." />}>
                  <NotificationSettings />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Shopify OAuth callback route (public) */}
          <Route
            path="/shopify/callback"
            element={
              <Suspense fallback={<LoadingFallback message="Processing Shopify Connection..." />}>
                <ShopifyCallback />
              </Suspense>
            }
          />
          {/* Developer admin routes - role-gated to ADMIN */}
          <Route
            path="/developer"
            element={
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <Suspense fallback={<LoadingFallback message="Loading Developer Dashboard..." />}>
                  <DeveloperOverview />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/developer/customers"
            element={
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <Suspense fallback={<LoadingFallback message="Loading Developer Clients..." />}>
                  <DeveloperCustomers />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/developer/billing"
            element={
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <Suspense fallback={<LoadingFallback message="Loading Developer Billing..." />}>
                  <DeveloperBilling />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/developer/shopify"
            element={
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <Suspense fallback={<LoadingFallback message="Loading Shopify Integration..." />}>
                  <DeveloperShopify />
                </Suspense>
              </ProtectedRoute>
            }
          />
          </Routes>
          </Suspense>
        </main>
        </MobileLayout>
        <CookieConsent />
        {process.env.NODE_ENV === 'development' && (
          <>
            <ConsoleCleaner />
            {/* <NavigationTester /> - Removed as requested: was causing crashes and getting in the way */}
          </>
        )}
        </TourProvider>
      </RouteErrorBoundary>
    </Router>
  )
}

export default App
